import React, { useState, useEffect } from 'react';
import { usersAPI, transportAPI } from '../services/api';
import ConfirmDialog from './ConfirmDialog';
import Toast from './Toast';
import SuccessPopup from './SuccessPopup';
import { pickPhotoWithSource } from '../utils/photoUpload';
import SidebarNav from './SidebarNav';

const AddUser = ({ onBack, onCancel, onNavigate, userRole = 'admin' }) => {
  const [formData, setFormData] = useState({
    supervisorName: '',
    phone: '',
    storeAllocated: 'mart', // Default value
    address: '',
    city: '',
    state: '',
    pincode: '',
    username: '',
    password: '',
    photo: null
  });
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [successCredentials, setSuccessCredentials] = useState(null);
  const [confirmState, setConfirmState] = useState({ open: false, message: '', onConfirm: null });
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [cities, setCities] = useState([]);

  // Fetch cities for dropdown
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const response = await transportAPI.getCities('');
        if (response.success) {
          setCities(response.cities || []);
        }
      } catch (err) {
        console.error('Error fetching cities:', err);
      }
    };
    fetchCities();
  }, []);

  const handleBack = () => {
    if (onNavigate) {
      onNavigate('users');
    } else if (onBack) {
      onBack();
    }
  };

  const handleHome = () => {
    if (onNavigate) {
      onNavigate('dashboard');
    }
  };

  const handleUsers = () => {
    if (onNavigate) {
      onNavigate('users');
    }
  };

  const handleProducts = () => {
    if (onNavigate) {
      onNavigate('products');
    }
  };

  const handleStaff = () => {
    if (onNavigate) {
      onNavigate('staff');
    }
  };

  const handleCustomers = () => {
    if (onNavigate) {
      onNavigate('customers');
    }
  };

  const handleSettings = () => {
    if (onNavigate) {
      onNavigate('settings');
    }
  };

  const handleCancel = () => {
    if (onNavigate) {
      onNavigate('users');
    } else if (onCancel) {
      onCancel();
    }
  };

  const handlePhotoUpload = async () => {
    try {
      setError('');
      const result = await pickPhotoWithSource({ quality: 80, allowEditing: true });
      
      if (result.cancelled) {
        return; // User cancelled, do nothing
      }
      
      if (!result.success) {
        setError(result.error || 'Failed to select photo');
        return;
      }
      
      if (result.file && result.preview) {
        setPhotoFile(result.file);
        setPhotoPreview(result.preview);
        setFormData(prev => ({ ...prev, photo: result.preview }));
      }
    } catch (err) {
      console.error('Photo upload error:', err);
      setError('Failed to upload photo. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const submitUser = async () => {
    setIsLoading(true);
    setError('');
    setSuccessMessage('');

    try {
      // Split supervisor name into first and last name for backend compatibility
      const nameParts = formData.supervisorName.trim().split(' ').filter(part => part.length > 0);
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ').trim() || null; // Use null if empty
      
      // Trim all values before sending
      const response = await usersAPI.create({
        firstName: firstName.trim(),
        lastName: lastName, // Can be null
        username: formData.username.trim(),
        password: formData.password,
        storeAllocated: formData.storeAllocated || 'mart',
        address: formData.address.trim() || null,
        city: formData.city.trim() || null,
        state: formData.state.trim() || null,
        pincode: formData.pincode.trim() || null,
        phone: formData.phone.trim(),
        photo: formData.photo || null
      });

      if (response.success) {
        setSuccessMessage('Supervisor created successfully!');
        setSuccessCredentials({ username: formData.username, password: formData.password });
      }
    } catch (err) {
      setError(err.message || 'Failed to create user. Please try again.');
      console.error('Create user error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setConfirmState({
      open: true,
      message: 'Are you sure you want to submit?',
      onConfirm: submitUser,
    });
  };

  return (
    <div className="dashboard-container">
      {/* Left Sidebar Navigation */}
      <SidebarNav onNavigate={onNavigate} userRole={userRole} activeKey="users" />

      {/* Main Content Area */}
      <div className="dashboard-main">
        <div className="add-user-container">
          {/* Header */}
          <header className="add-user-header">
            <button className="back-btn" onClick={handleBack}>
              <i className="fas fa-arrow-left"></i>
            </button>
            <div className="header-content">
              <h1 className="page-title">Add Supervisor</h1>
            </div>
          </header>

          {/* Main Content */}
          <main className="add-user-content">
            {/* Photo Upload */}
            <div className="photo-upload-section">
              {photoPreview ? (
                <div style={{ position: 'relative', width: '120px', height: '120px', margin: '0 auto' }}>
                  <img 
                    src={photoPreview} 
                    alt="Supervisor preview" 
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '50%',
                      objectFit: 'cover',
                      border: '3px solid #dc3545'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setPhotoPreview(null);
                      setPhotoFile(null);
                      setFormData(prev => ({ ...prev, photo: null }));
                      const fileInput = document.getElementById('supervisorPhoto');
                      if (fileInput) fileInput.value = '';
                    }}
                    style={{
                      position: 'absolute',
                      top: '-8px',
                      right: '-8px',
                      background: '#dc3545',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '50%',
                      width: '28px',
                      height: '28px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                    }}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              ) : (
                <div className="photo-placeholder">
                  <i className="fas fa-camera"></i>
                </div>
              )}
              <button
                type="button"
                className="upload-photo-btn"
                onClick={handlePhotoUpload}
              >
                <i className="fas fa-camera"></i>
                <span>{photoPreview ? 'Change photo' : 'Upload photo'}</span>
              </button>
            </div>

            <form onSubmit={handleSubmit} className="add-user-form">
                {/* All fields in 3-column grid without section titles */}
                <div className="form-section">
                  <div className="form-grid three-col">
                    {/* Row 1: Name, Phone Number */}
                    <div className="form-group">
                      <label htmlFor="supervisorName">Supervisor name</label>
                      <input
                        type="text"
                        id="supervisorName"
                        name="supervisorName"
                        className="form-input"
                        placeholder="Enter supervisor name"
                        value={formData.supervisorName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>

                    <div className="form-group">
                      <label htmlFor="phone">Phone number</label>
                      <div className="input-wrapper">
                        <i className="fas fa-phone input-icon"></i>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          className="form-input"
                          placeholder="Enter phone number"
                          value={formData.phone}
                          onChange={handleInputChange}
                          required
                        />
                      </div>
                    </div>

                    {/* Row 2: Street, City, State */}
                    <div className="form-group">
                      <label htmlFor="address">Street</label>
                      <div className="input-wrapper">
                        <i className="fas fa-map-marker-alt input-icon"></i>
                        <input
                          type="text"
                          id="address"
                          name="address"
                          className="form-input"
                          placeholder="Enter street address"
                          value={formData.address}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="city">City</label>
                      <div className="input-wrapper">
                        <i className="fas fa-city input-icon"></i>
                        <input
                          type="text"
                          id="city"
                          name="city"
                          className="form-input"
                          placeholder="Enter or select city"
                          value={formData.city}
                          onChange={handleInputChange}
                          list="cities-list"
                        />
                        <datalist id="cities-list">
                          {cities.map((city, index) => (
                            <option key={index} value={city} />
                          ))}
                        </datalist>
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="state">State</label>
                      <div className="input-wrapper">
                        <i className="fas fa-map input-icon"></i>
                        <input
                          type="text"
                          id="state"
                          name="state"
                          className="form-input"
                          placeholder="Enter state"
                          value={formData.state}
                          onChange={handleInputChange}
                        />
                      </div>
                    </div>

                    {/* Row 3: Pincode (alone) */}
                    <div className="form-group">
                      <label htmlFor="pincode">Pincode</label>
                      <div className="input-wrapper">
                        <i className="fas fa-mail-bulk input-icon"></i>
                        <input
                          type="text"
                          id="pincode"
                          name="pincode"
                          className="form-input"
                          placeholder="Enter pincode"
                          value={formData.pincode}
                          onChange={handleInputChange}
                          required
                          maxLength="10"
                        />
                  </div>
                </div>

                    {/* Row 4: Username and Password (2 columns) */}
                    <div className="form-group">
                      <label htmlFor="username">Username</label>
                      <div className="input-wrapper">
                        <i className="fas fa-user input-icon"></i>
                      <input
                        type="text"
                        id="username"
                        name="username"
                        className="form-input"
                          placeholder="Enter username"
                        value={formData.username}
                        onChange={handleInputChange}
                        required
                      />
                      </div>
                    </div>

                    <div className="form-group">
                      <label htmlFor="password">Password</label>
                      <div className="input-wrapper password-wrapper">
                        <i className="fas fa-lock input-icon"></i>
                        <input
                          type={showPassword ? 'text' : 'password'}
                          id="password"
                          name="password"
                          className="form-input"
                          placeholder="Enter password"
                          value={formData.password}
                          onChange={handleInputChange}
                          required
                        />
                        <button
                          type="button"
                          className="password-toggle"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className={showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'}></i>
                        </button>
                      </div>
                    </div>

                    {/* Store field - hidden but required for submission */}
                    <input
                      type="hidden"
                      id="storeAllocated"
                      name="storeAllocated"
                      value={formData.storeAllocated || 'mart'}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <Toast message={error} type="error" onClose={() => setError('')} />

                {/* Action Buttons */}
                <div className="form-actions">
                  <button type="submit" className="create-user-btn" disabled={isLoading}>
                    {isLoading ? 'Creating...' : 'Create user'}
                  </button>
                  <button type="button" className="cancel-btn" onClick={handleCancel}>
                    Cancel and go back
                  </button>
                </div>
              </form>
          </main>
        </div>
      </div>

      <ConfirmDialog
        isOpen={confirmState.open}
        title="Confirm Submission"
        message={confirmState.message}
        confirmText="Yes, Submit"
        cancelText="Cancel"
        onConfirm={() => {
          setConfirmState({ open: false, message: '', onConfirm: null });
          if (confirmState.onConfirm) confirmState.onConfirm();
        }}
        onCancel={() => setConfirmState({ open: false, message: '', onConfirm: null })}
      />

      <SuccessPopup
        message={successMessage}
        credentials={successCredentials}
        onClose={() => {
          setSuccessMessage('');
          setSuccessCredentials(null);
          handleCancel();
        }}
      />
    </div>
  );
};

export default AddUser;